import {
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
    createAction,
    createSelector,
    AnyAction,
} from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import {
    Identifiable,
    ID,
    LoadingErrorMaybe,
    isUsable,
    ImageWarnings,
    ImageProcessingState,
    PreviouslyParsedTagValues,
    AnyAction as Any,
} from "@nova/constants"
import { createHooks } from "./upload-processing-hooks"
import { memoize, get, set } from "nodash"
import { TypedEventEmitter } from "@nova/typed-event-emitter"
import { useState, useEffect } from "react"

export const flightIdsWithUnsavedFiles = new Set()

export const stagedFiles = {} as Record<string, File>

export type UploadState<T extends Identifiable> = LoadingErrorMaybe<T, SerializedError>

export type SetState = LoadingErrorMaybe<{ id: ID; uploads: ID[] }, SerializedError>

export type MultifileUploadState<T extends Identifiable> = {
    uploads: Record<ID, UploadState<T>>
    sets: Record<ID, SetState>
    pending: Record<ID, Record<string, number>>
}

export type MultifileUploadApi<T> = {
    // this is the api to get a set of uploads
    addUploadToProject: ((setId: string, upload: T) => AnyAction) | null
}

class SpeedEmitter extends TypedEventEmitter<string> {
    public speeds = {} as Record<ID, Record<ID, number>>
    setSpeed(flightId: string, imageId: string, speed: number) {
        this.speeds[flightId] ??= {}
        this.speeds[flightId][imageId] = speed
        this.emit(flightId + "updated")
    }
    calculateAverageSpeed(flightId: string) {
        const values = Object.values(this.speeds[flightId])
        if (values.length === 0) return 0
        const sum = values.reduce((acc, val) => acc + val, 0)
        return Math.round(sum / values.length)
    }
}

class ProgressEmitter extends TypedEventEmitter<string> {
    public progress = {} as Record<ID, number>
    setProgress(flightId: string, progress: number) {
        this.progress[flightId] = progress
        this.emit(flightId + "updated")
    }
    getProgress(flightId: string) {
        return this.progress[flightId]
    }
}

export const speedStream = new SpeedEmitter()
export const singleFileProgressStream = new ProgressEmitter()

export const useUploadSpeedForFlightId = (flightId: string) => {
    const [averageSpeed, setAverageSpeed] = useState<number>(0)

    useEffect(() => {
        const onUpdate = () => {
            setAverageSpeed(speedStream.calculateAverageSpeed(flightId))
        }
        speedStream.on(flightId + "updated", onUpdate)
        return () => {
            speedStream.off(flightId + "updated", onUpdate)
        }
    }, [flightId])

    return averageSpeed
}

export const useProgressForSingleUploadFlightId = (flightId: string) => {
    const [progress, setProgress] = useState<number>(
        () => singleFileProgressStream.getProgress(flightId) ?? 0
    )

    useEffect(() => {
        setProgress(singleFileProgressStream.getProgress(flightId) ?? 0)
        const onUpdate = () => {
            setProgress(singleFileProgressStream.getProgress(flightId))
        }
        singleFileProgressStream.on(flightId + "updated", onUpdate)
        return () => {
            singleFileProgressStream.off(flightId + "updated", onUpdate)
        }
    }, [flightId])

    return progress
}

export function createMultifileUploadSlice<
    // name inside the root state - should be unique across the application
    N extends string,
    // the type of an individual upload job
    T extends Identifiable,
    // the type of non-serializable state (such as files)
    M extends {
        setID: string
        organizationID: string
        flightID: string
        fireID: string
        file: File
        state?: T
    },
>(
    // this is the name to use
    name: N,

    // initial state for an upload
    createInitial: (m: M) => T,

    // the underlying api that the multifile upload sits on top of
    api: MultifileUploadApi<T>,

    // this is the set of processing steps to undergo
    processing?: (
        state: T,
        meta: M,
        update: (intermediate: T & { previousId?: string }, onCompletion?: () => void) => void,
        hooks: ReturnType<ReturnType<typeof createHooks>["hooks"]>
    ) => Promise<T | undefined>,

    // an additional processing step that happens when calculating the set summary
    warningProcessor?: (uploads: T[]) => { warnings?: ImageWarnings; error?: string },
    deleteOnStage = false
) {
    const { hooks, signal } = createHooks(name)

    const selectSet = (id: ID) => (s: MultifileUploadState<T>) => isUsable(s.sets[id])
    const selectAllUploads = (s: MultifileUploadState<T>) => s.uploads
    const selectUploadsForSet = memoize((id: ID) =>
        createSelector([selectSet(id), selectAllUploads], (set, uploads) => {
            if (!set) return undefined
            return set.uploads.map((id) => uploads[id])
        })
    )
    const selectUploadIdsForSet = memoize((id: ID) =>
        createSelector([selectSet(id)], (set) => {
            if (!set) return []
            return set.uploads
        })
    )
    const selectCancelledUploadIdsForSet = memoize((id: ID) =>
        createSelector([selectUploadsForSet(id)], (set) => {
            if (!set) return []
            return set
                .map((s: UploadState<T>) => {
                    const isCancelled = get(s, "isCancelled")
                    return isCancelled ? s.id : null
                })
                .filter((s: ID | null): s is ID => Boolean(s))
        })
    )
    const selectUploadsWithStates = memoize((id: ID) =>
        createSelector([selectUploadsForSet(id)], (set) => {
            if (!set) return undefined
            const states: Record<string, ImageProcessingState> = {}
            set.forEach((s: UploadState<T>) => {
                const state = get(s, "state") as ImageProcessingState | undefined
                if (state) states[s.id] = state
            })
            return states
        })
    )

    const selectors = withRoot((r: Any) => get(r, `entities.${name}`), {
        selectUpload: (id: ID) => (s) => s.uploads[id],
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectUploadsWithStates,
        selectAllSets: () => (s) => s.sets,
        selectAllUploads: () => (s) => s.uploads,
        selectPendingForSet: (id: ID, name: string) => (s) => {
            const pendingUpload = s.pending[id]?.[name]
            return pendingUpload > 0
        },
        selectUploadsForSetSummary: memoize(
            (id: ID, getState: (t: T) => ImageProcessingState, isDone: (t: T) => boolean) =>
                createSelector([selectUploadsForSet(id)], (uploads) => {
                    if (!uploads) {
                        return {
                            loading: true,
                            stateCount: undefined,
                            uploadCount: undefined,
                            errorCount: undefined,
                            pendingCount: undefined,
                            unsupportedCount: undefined,
                            warnings: undefined,
                        }
                    }
                    const states: Record<number, number> = {}
                    let pending = 0
                    let errorCount = 0
                    let unsupportedCount = 0
                    const usableUploads = []
                    for (let i = 0; i < uploads.length; i++) {
                        const upload = isUsable(uploads[i]) as T & {
                            unsupported?: boolean
                            warnings?: ImageWarnings
                            tags: PreviouslyParsedTagValues
                            state: ImageProcessingState
                        }
                        if (!!upload) {
                            usableUploads.push(upload)
                            const state = getState(upload)
                            states[state] = (states[state] || 0) + 1
                            if (!isDone(upload)) {
                                pending++
                            }
                            if (upload.unsupported) {
                                unsupportedCount++
                            }
                        } else {
                            const unusableUpload = uploads[i]
                            if (unusableUpload && "error" in unusableUpload) {
                                errorCount++
                            }
                        }
                    }
                    const warnings =
                        usableUploads.every(
                            (upload) => upload.state === ImageProcessingState.ProcessingDone
                        ) && warningProcessor
                            ? warningProcessor(usableUploads)
                            : undefined
                    return {
                        loading: false,
                        stateCount: states,
                        uploadCount: uploads.length,
                        errorCount,
                        pendingCount: pending,
                        unsupportedCount,
                        ...warnings,
                    }
                })
        ),
        selectSet: (id: ID) => (s) => s.sets[id],
    })

    const update = createAction<T & { previousId?: string }>(`entities/${name}/update`)
    const updateMany = createAction<(T & { previousId?: string })[]>(`entities/${name}/updateMany`)
    const adjustPending = createAction<{ name: string; setid: string; adjustment: number }>(
        `entities/${name}/pending`
    )

    // Stages upload entries with no backing `File` — e.g. images already being
    // copied server-side that should render in PreviewFiles like a normal
    // upload, without running the (file-dependent) `processing` pipeline.
    const stageRemote = createAction<{ setID: ID; entries: T[] }>(`entities/${name}/stage-remote`)

    const deleteUpload = createAsyncThunk(
        `entities/${name}/delete-upload`,
        async ({ uploadId }: { setId: string; uploadId: string }) => {
            signal("delete", uploadId)
        }
    )

    const deleteUploadSetCommitted = createAction<{ flightId: ID }>(
        `entities/${name}/deleteUploadSet`
    )

    const deleteUploadSet =
        ({ flightId }: { flightId: ID }) =>
        (dispatch: (action: AnyAction) => void, getState: () => unknown) => {
            const state = getState() as Record<string, unknown>
            const sliceState = get(state, `entities.${name}`) as MultifileUploadState<T> | undefined
            const setState = sliceState?.sets?.[flightId]
            const uploadIds = isUsable(setState)
                ? ((setState as unknown as { uploads?: ID[] })?.uploads ?? [])
                : []
            uploadIds.forEach((uploadId: ID) => dispatch(signal("delete", uploadId)))
            dispatch(deleteUploadSetCommitted({ flightId }))
        }

    // so the way this works is when we stage a file, this increments; when we finish processing, it decrements
    // anytime this > 0, there are unprocessed files
    const pending = {} as Record<string, boolean>

    const collect = <T>(
        fn: (items: T[]) => void,
        time: number
    ): ((item: T, onCompletion?: () => void) => void) => {
        let timeout: ReturnType<typeof setTimeout> | undefined
        const items = [] as T[]
        const callbacks = [] as (() => void)[]
        return (item: T, onCompletion?: () => void) => {
            items.push(item)
            if (onCompletion) callbacks.push(onCompletion)
            if (timeout) return
            else {
                timeout = setTimeout(() => {
                    fn(items)
                    items.length = 0
                    // Execute all callbacks after dispatch.
                    callbacks.forEach((callback) => callback())
                    callbacks.length = 0
                    timeout = undefined
                }, time)
            }
        }
    }

    const stageMany = createAsyncThunk(
        `entities/${name}/stage-files`,
        async ({ meta, onFinish }: { meta: M[]; onFinish?: () => void }, thunkApi) => {
            const updateBatched = collect(
                (newstate: T[]) => void thunkApi.dispatch(updateMany(newstate)),
                500
            )
            const results = await Promise.all(
                meta.map(async (m) => {
                    const state = m.state!
                    m.state = state
                    stagedFiles[state.id] = m.file
                    pending[state.id] = true

                    try {
                        const processingHooks = hooks(state.id, {
                            addPending: () => {},
                            removePending: () => {},
                        })
                        const newstate = await (processing
                            ? processing(state, m, updateBatched, processingHooks)
                            : Promise.resolve(state))
                        if (!newstate) return
                        if (deleteOnStage) {
                            thunkApi.dispatch(
                                actions.deleteUploadSync({ uploadId: newstate.id, setId: m.setID })
                            )
                            return
                        }
                    } catch (err) {
                        console.error(err)
                        return { ...state, error: err as SerializedError }
                    } finally {
                        delete pending[state.id]
                    }
                })
            )
            if (onFinish) onFinish()
            return results
        }
    )

    const { actions, reducer } = createSlice({
        name,
        initialState: {
            uploads: {},
            sets: {},
            pending: {},
        } as MultifileUploadState<T>,
        reducers: {
            deleteUploadSync: (state, action) => {
                delete state.uploads[action.payload.uploadId]
                delete pending[action.payload.uploadId]
                const set = isUsable(state.sets[action.payload.setId] as SetState)
                if (set) {
                    set.uploads = set.uploads.filter((i) => i !== action.payload.uploadId)
                    state.sets[action.payload.setId] = set as Draft<SetState>
                } else {
                    console.warn("@nova/slice-multifile-upload -- deleted upload from unknown set")
                }
            },
        },
        extraReducers: (builder) =>
            builder
                .addCase(update, (state, action) => {
                    const { previousId, ...t } = action.payload
                    // this happens when the id is updated; this can happen when it actually gets persisted to the server
                    if (previousId != null && t.id !== previousId && state.uploads[previousId]) {
                        if (pending[previousId]) {
                            pending[t.id] = pending[previousId]
                            delete pending[previousId]
                        }
                        // remap the previous id to the new value
                        state.uploads[t.id] = state.uploads[previousId]
                        delete state.uploads[previousId]
                        // update links to point to the new value
                        for (const setId in state.sets) {
                            const set = state.sets[setId]
                            if ("uploads" in set) {
                                set.uploads = set.uploads.map((id) =>
                                    id === previousId ? t.id : id
                                )
                                state.sets[setId] = set
                            }
                        }
                    }

                    // the case where it isn't is if we have some async process that happens after we delete
                    // for example:
                    // A - check isUsable
                    // A - start async
                    // B - delete state
                    // A - update state
                    // in this scenario, this check makes sure we don't re-introduce a deleted value
                    if (isUsable(state.uploads[t.id])) {
                        state.uploads[t.id] = t
                    }
                })
                .addCase(updateMany, (state, action) => {
                    action.payload.forEach((item) => {
                        const { previousId, ...t } = item
                        // this happens when the id is updated; this can happen when it actually gets persisted to the server
                        if (
                            previousId != null &&
                            t.id !== previousId &&
                            state.uploads[previousId]
                        ) {
                            if (pending[previousId]) {
                                pending[t.id] = pending[previousId]
                                delete pending[previousId]
                            }
                            // remap the previous id to the new value
                            state.uploads[t.id] = state.uploads[previousId]
                            delete state.uploads[previousId]
                            // update links to point to the new value
                            for (const setId in state.sets) {
                                const set = state.sets[setId]
                                if ("uploads" in set) {
                                    set.uploads = set.uploads.map((id) =>
                                        id === previousId ? t.id : id
                                    )
                                    state.sets[setId] = set
                                }
                            }
                        }
                        if (isUsable(state.uploads[t.id])) {
                            state.uploads[t.id] = t as (typeof state.uploads)[number]
                        }
                    })
                })
                .addCase(adjustPending, (state, action) => {
                    const { name, setid, adjustment } = action.payload

                    // unravel the nested structure, defaulting when we are missing values
                    const pendingForSet = state.pending[setid] || {}
                    let pendingForName = pendingForSet[name] || 0

                    // make the adjustment
                    pendingForName += adjustment

                    // update state
                    pendingForSet[name] = pendingForName
                    state.pending[setid] = pendingForSet
                })

                .addCase(stageMany.pending, (state, { meta }) => {
                    // Add this flight ID to the set of flight IDs that have unsaved files -- to warn user about unsafe browser navigation.
                    const flightID = meta.arg.meta.at(0)?.flightID
                    if (flightID !== undefined) flightIdsWithUnsavedFiles.add(flightID)
                    meta.arg.meta.forEach((meta) => {
                        const { setID } = meta
                        const t = createInitial(meta)
                        meta.state = t
                        if (!state.sets[setID]) {
                            state.sets[setID] = { id: setID, uploads: [] }
                        }
                        const set = isUsable(state.sets[setID] as SetState)
                        if (set) {
                            state.uploads[t!.id] = t as unknown as (typeof state.uploads)[string]
                            set.uploads.push(t!.id)
                        } else {
                            console.warn(
                                "@nova/slice-multifile-upload -- trying to stage an image to a set that hasnt been loaded",
                                t!.id,
                                setID
                            )
                        }
                    })
                })
                .addCase(stageMany.fulfilled, (state, { meta, payload }) => {
                    // Remove this flight ID from the set of flight IDs that have unsaved files.
                    const flightID = meta.arg.meta.at(0)?.flightID
                    if (flightID !== undefined) flightIdsWithUnsavedFiles.delete(flightID)
                    payload.forEach((payload, i) => {
                        if (payload && !isUsable(payload)) {
                            state.uploads[payload.id] = {
                                ...state.uploads[payload.id],
                                error: payload.error as SerializedError,
                            }
                        } else if (
                            payload &&
                            isUsable(state.uploads[payload.id]) &&
                            isUsable(state.sets[meta.arg.meta[i].setID]) &&
                            deleteOnStage
                        ) {
                            const set = isUsable(state.sets[meta.arg.meta[i].setID] as SetState)
                            const idx = set!.uploads.findIndex((e) => e === payload.id)
                            if (idx !== -1) set!.uploads.splice(idx, 1)
                            if (deleteOnStage) delete state.uploads[payload.id]
                        }
                    })
                })
                .addCase(stageRemote, (state, action) => {
                    const { setID, entries } = action.payload
                    if (!state.sets[setID]) {
                        state.sets[setID] = { id: setID, uploads: [] }
                    }
                    const set = isUsable(state.sets[setID] as SetState)
                    if (!set) {
                        console.warn(
                            "@nova/slice-multifile-upload -- trying to stage remote entries to a set that hasnt been loaded",
                            setID
                        )
                        return
                    }
                    entries.forEach((t) => {
                        // Don't clobber an entry already tracked for this set
                        if (!set.uploads.includes(t.id) || !isUsable(state.uploads[t.id])) {
                            state.uploads[t!.id] = t as unknown as (typeof state.uploads)[string]
                        }
                        if (!set.uploads.includes(t.id)) set.uploads.push(t.id)
                    })
                    state.sets[setID] = set as Draft<SetState>
                })
                .addCase(deleteUpload.pending, (state, action) => {
                    state.uploads[action.meta.arg.uploadId] = {
                        id: action.meta.arg.uploadId,
                        loading: true,
                    }
                })
                .addCase(deleteUpload.fulfilled, (state, action) => {
                    delete state.uploads[action.meta.arg.uploadId]
                    delete pending[action.meta.arg.uploadId]
                    const set = isUsable(state.sets[action.meta.arg.setId] as SetState)
                    if (set) {
                        set.uploads = set.uploads.filter((i) => i != action.meta.arg.uploadId)
                        state.sets[action.meta.arg.setId] = set as Draft<SetState>
                    } else {
                        console.warn(
                            "@nova/slice-multifile-upload -- deleted upload from unknown set"
                        )
                    }
                })
                .addCase(deleteUpload.rejected, (state, action) => {
                    state.uploads[action.meta.arg.uploadId] = {
                        id: action.meta.arg.uploadId,
                        error: action.error,
                    }
                })
                .addCase(deleteUploadSetCommitted, (state, action) => {
                    const target = action.payload.flightId
                    delete state.sets[target]
                    const filteredUploads: Record<string, { id: string }> = {}
                    Object.values(state.uploads).filter((upload) => {
                        const uploadFlightId = get(upload, "flightId")
                        if (uploadFlightId !== target) filteredUploads[upload.id] = upload
                    })
                    set(state, "uploads", filteredUploads)
                }),
    })

    return {
        hooks,
        actions: {
            ...actions,
            deleteUpload,
            signal,
            update,
            updateMany,
            stageFiles: stageMany,
            stageRemote,
            deleteUploadSet,
        },
        reducer: { [name]: reducer } as Record<N, typeof reducer>,
        selectors: {
            ...selectors,
        },
        hasPendingWork: () => {
            return Object.keys(pending).length > 0
        },
    }
}

export const getFileNameFromImageId = (imageId: string) => {
    const split = imageId?.split(".")
    if (!split) return ""
    return split[0] + "." + split.at(-1)
}
